/**
 * §P.1 · La ficha del alumno, con sus tutores.
 *
 * La parte que importa de verdad aquí son **los tutores**, porque es el círculo
 * completo de `BFF_BO.md` §7.1: `POST /students/{id}/guardians` es lo que mete a
 * una familia en la app, y la familia canjea en `/auth/invitations/verify`.
 *
 * Y de los tutores, el dato que la pantalla tiene que enseñar sí o sí es
 * **`membership_status`**: `invited` es «se mandó el correo y esa persona todavía
 * no ha entrado»; `active` es «ya está dentro». Sin eso, un club acaba llamando
 * por teléfono a las 120 familias para averiguar quién aceptó.
 *
 * El **modo abuelo** (`access_level: 'basic'`) se explica con lo que implica de
 * verdad —ve progreso y avisos; no ve pagos ni ficha médica—, no con su nombre
 * técnico, que a un cliente no le dice nada.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { etagDe } from '@/shared/api/concurrencia';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import { EstadoDeAlumno } from '@/club/alumnos/EstadoDeAlumno';
import { FormularioDeAlumno } from '@/club/alumnos/FormularioDeAlumno';
import {
  etiquetaDeParentesco,
  FormularioDeTutor,
} from '@/club/alumnos/tutores/FormularioDeTutor';
import { RevocarTutor } from '@/club/alumnos/tutores/RevocarTutor';
import { InvitarPorWhatsApp } from '@/club/alumnos/tutores/InvitarPorWhatsApp';
import type { DesdeTutor } from '@/club/pagadores/FormularioDePagador';
import {
  puedeEditarAlumnos,
  puedeEditarContactos,
  puedeEditarCuota,
  puedeOtorgarGrados,
  puedeVerCuota,
  puedeVerGrados,
  puedeEditarFichaMedica,
  puedeVerContactos,
  puedeVerFichaMedica,
  type Rol,
} from '@/club/permisos';
import { CuotaDelAlumno } from '@/club/cuotas/CuotaDelAlumno';
import { PasaporteDelAlumno } from '@/club/grados/PasaporteDelAlumno';
import { FichaMedica } from '@/club/alumnos/salud/FichaMedica';
import { ContactosDeEmergencia } from '@/club/alumnos/salud/ContactosDeEmergencia';
import { AbrirConversacion } from '@/club/avisos/AbrirConversacion';
import { Lesiones } from '@/club/alumnos/salud/Lesiones';
import { PapelesDelAlumno } from '@/club/alumnos/administracion/PapelesDelAlumno';
import { TarjetaDeFichaje } from '@/club/alumnos/administracion/TarjetaDeFichaje';
import { Tallas } from '@/club/alumnos/administracion/Tallas';
import { ControlDePeso } from '@/club/competicion/ControlDePeso';
import { PasaporteImprimible } from '@/club/alumnos/administracion/PasaporteImprimible';
import {
  nombreCompleto,
  nombreDeTutor,
  type Alumno,
  type Tutor,
} from '@/club/alumnos/tipos';

export function FichaDeAlumno({
  club,
  alumnoId,
  rol,
  suspendido,
  alCerrar,
  alCrearPagador,
  alAbrirPagador,
  alVerElChat,
  alVerSuAsistencia,
}: {
  club: string;
  alumnoId: string;
  rol: Rol;
  suspendido: boolean;
  alCerrar: () => void;
  /** Al abrir una conversación, lleva al chat: es donde vive desde ese momento. */
  alVerElChat: () => void;
  /**
   * Crear un pagador a partir de este tutor.
   *
   * Es lo que pide `BFF_BO.md` §7.4 —«al dar de alta a alguien que ya es tutor
   * del club, ofrecedle la cuenta que ya tiene en lugar de dejarlo suelto»— y es
   * el único sitio donde se puede: `GET /memberships` es solo del personal y los
   * tutores no salen ahí.
   */
  alCrearPagador: (desde: DesdeTutor) => void;
  alAbrirPagador: (pagadorId: string) => void;
  /**
   * «Ver su asistencia», que lleva a la consulta transversal ya filtrada por él.
   *
   * No se enseña aquí dentro por una razón de tamaño: la asistencia de un alumno son
   * cien filas y esta ficha ya tiene tutores, cuota, salud, papeles y grados. Lo que
   * hacía falta era **que se pudiera llegar**, que hasta el 18-09-2026 no se podía.
   */
  alVerSuAsistencia: () => void;
}) {
  const puedeEscribir = puedeEditarAlumnos(rol);
  const [editando, setEditando] = useState(false);
  /*
   * Qué se está haciendo con los tutores. Un solo estado y no tres banderas: así
   * no puede haber un «editando y revocando a la vez», que es el estado imposible
   * que se cuela cuando son booleanos sueltos.
   */
  const [escribiendo, setEscribiendo] = useState(false);
  const [conTutores, setConTutores] = useState<
    | { readonly que: 'nada' }
    | { readonly que: 'anadir' }
    | { readonly que: 'editar'; readonly tutor: Tutor }
    | { readonly que: 'revocar'; readonly tutor: Tutor }
    | { readonly que: 'whatsapp'; readonly tutor: Tutor }
  >({ que: 'nada' });
  /*
   * El `ETag` viaja con la ficha, no aparte.
   *
   * `PATCH` sin `If-Match` responde `428`, así que la precondición es parte del
   * dato: guardar el alumno sin su `ETag` sería guardar algo que no se puede
   * editar. Por eso la consulta devuelve los dos juntos y no hay forma de tener
   * uno sin el otro.
   */
  const alumno = useQuery({
    queryKey: ['alumno', club, alumnoId],
    queryFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .get();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return { alumno: r.data as unknown as Alumno, etag: etagDe(r) };
    },
  });

  const tutores = useQuery({
    queryKey: ['tutores', club, alumnoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .guardians.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Tutor[];
    },
  });

  return (
    <section className="ficha">
      <header className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Alumnos
        </button>
        <div className="hueco-acciones">
          {/* Lo lee cualquiera que entre a la ficha: es su asistencia, no su dinero. */}
          <button
            type="button"
            className="btn-plano"
            onClick={alVerSuAsistencia}
          >
            Ver su asistencia
          </button>
          {/* Editar es de `owner` y `admin`; al entrenador no se le enseña. */}
          {puedeEscribir && alumno.data && !editando && (
            <button type="button" onClick={() => setEditando(true)}>
              Editar
            </button>
          )}
        </div>
      </header>

      {alumno.data && editando && (
        <FormularioDeAlumno
          club={club}
          alumno={alumno.data.alumno}
          etag={alumno.data.etag}
          suspendido={suspendido}
          alGuardar={() => setEditando(false)}
          alCancelar={() => setEditando(false)}
        />
      )}

      {alumno.isPending && <Cargando filas={3} etiqueta="Cargando la ficha" />}

      {alumno.isError && (
        <PanelError
          problema={aProblema(alumno.error)}
          reintentar={() => void alumno.refetch()}
        />
      )}

      {alumno.data && !editando && (
        <>
          <h1>{nombreCompleto(alumno.data.alumno)}</h1>
          <dl className="datos">
            <div>
              <dt>Número de socio</dt>
              <dd>{alumno.data.alumno.code ?? '—'}</dd>
            </div>
            <div>
              <dt>Edad</dt>
              <dd>{alumno.data.alumno.age ?? '—'}</dd>
            </div>
            <div>
              <dt>Alta</dt>
              <dd>{fecha(alumno.data.alumno.joined_on)}</dd>
            </div>
            <div>
              <dt>Estado</dt>
              <dd>
                <EstadoDeAlumno estado={alumno.data.alumno.status} />
              </dd>
            </div>
          </dl>

          <div className="seccion seccion-fila">
            <h2>Tutores</h2>
            {puedeEscribir && conTutores.que === 'nada' && tutores.data && (
              <span className="tutor-acciones" style={{ margin: 0 }}>
                {/*
                  §P.6 · Escribir a la familia se abre **desde aquí** y no desde una
                  lista de personas: los destinatarios tienen que ser tutores de este
                  alumno, y así lo son por construcción.
                */}
                {tutores.data.length > 0 && (
                  <button
                    type="button"
                    className="btn-plano"
                    onClick={() => setEscribiendo(true)}
                  >
                    Escribir a la familia
                  </button>
                )}
                <button
                  type="button"
                  className="btn-plano"
                  onClick={() => setConTutores({ que: 'anadir' })}
                >
                  Añadir tutor
                </button>
              </span>
            )}
          </div>

          {escribiendo && tutores.data && (
            <AbrirConversacion
              club={club}
              alumnoId={alumnoId}
              nombreDelAlumno={nombreCompleto(alumno.data.alumno)}
              tutores={tutores.data}
              alAbrir={() => {
                setEscribiendo(false);
                alVerElChat();
              }}
              alCancelar={() => setEscribiendo(false)}
            />
          )}

          {conTutores.que === 'anadir' && (
            <FormularioDeTutor
              club={club}
              alumnoId={alumnoId}
              nombreDelAlumno={nombreCompleto(alumno.data.alumno)}
              nombreDePila={alumno.data.alumno.first_name}
              edadDelAlumno={alumno.data.alumno.age}
              tutor={null}
              alTerminar={() => setConTutores({ que: 'nada' })}
              alCancelar={() => setConTutores({ que: 'nada' })}
            />
          )}

          {conTutores.que === 'editar' && (
            <FormularioDeTutor
              club={club}
              alumnoId={alumnoId}
              nombreDelAlumno={nombreCompleto(alumno.data.alumno)}
              nombreDePila={alumno.data.alumno.first_name}
              edadDelAlumno={alumno.data.alumno.age}
              tutor={conTutores.tutor}
              alTerminar={() => setConTutores({ que: 'nada' })}
              alCancelar={() => setConTutores({ que: 'nada' })}
            />
          )}

          {conTutores.que === 'revocar' && (
            <RevocarTutor
              club={club}
              alumnoId={alumnoId}
              nombreDelAlumno={nombreCompleto(alumno.data.alumno)}
              tutor={conTutores.tutor}
              alTerminar={() => setConTutores({ que: 'nada' })}
              alCancelar={() => setConTutores({ que: 'nada' })}
            />
          )}

          {conTutores.que === 'whatsapp' && (
            <InvitarPorWhatsApp
              club={club}
              alumnoId={alumnoId}
              tutor={conTutores.tutor}
              alCerrar={() => setConTutores({ que: 'nada' })}
            />
          )}

          {tutores.isPending && (
            <Cargando filas={2} etiqueta="Cargando tutores" />
          )}

          {tutores.isError && (
            <PanelError
              problema={aProblema(tutores.error)}
              reintentar={() => void tutores.refetch()}
            />
          )}

          {conTutores.que === 'nada' && tutores.data?.length === 0 && (
            <Vacio
              titulo="Este alumno no tiene tutores"
              explicacion="Añadir un tutor le manda una invitación a la app de familias. Es lo que hace que el padre o la madre vea la asistencia, los grados y los avisos."
              {...(puedeEscribir
                ? {
                    accion: (
                      <button
                        type="button"
                        onClick={() => setConTutores({ que: 'anadir' })}
                      >
                        Añadir tutor
                      </button>
                    ),
                  }
                : {})}
            />
          )}

          {conTutores.que === 'nada' &&
            tutores.data &&
            tutores.data.length > 0 && (
              <ul className="tutores">
                {tutores.data.map((t) => (
                  <li key={t.user_id}>
                    <div className="tutor-nombre">
                      <strong>{nombreDeTutor(t)}</strong>
                      {t.is_primary && (
                        <span className="chapa">Contacto principal</span>
                      )}
                    </div>

                    <p className="tenue">
                      {etiquetaDeParentesco(t.relationship)}
                      {t.email ? ` · ${t.email}` : ''}
                    </p>

                    {puedeEscribir && (
                      <div className="tutor-acciones">
                        {/*
                          La misma invitación, por otro camino: el correo ya
                          salió. Solo mientras no ha entrado.
                        */}
                        {t.membership_status === 'invited' && (
                          <button
                            type="button"
                            className="btn-plano"
                            onClick={() =>
                              setConTutores({ que: 'whatsapp', tutor: t })
                            }
                          >
                            Enviar por WhatsApp
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-plano"
                          onClick={() =>
                            setConTutores({ que: 'editar', tutor: t })
                          }
                        >
                          Editar
                        </button>
                        {/*
                          §7.4: «al dar de alta a alguien que ya es tutor del
                          club, ofrecedle la cuenta que ya tiene en lugar de
                          dejarlo suelto». Este botón es el único sitio donde eso
                          es posible: los tutores no salen en `GET /memberships`.
                        */}
                        <button
                          type="button"
                          className="btn-plano"
                          onClick={() =>
                            alCrearPagador({
                              user_id: t.user_id,
                              legal_name: nombreDeTutor(t),
                              email: t.email,
                            })
                          }
                        >
                          Hacer pagador
                        </button>
                        <button
                          type="button"
                          className="btn-plano"
                          onClick={() =>
                            setConTutores({ que: 'revocar', tutor: t })
                          }
                        >
                          Quitar acceso
                        </button>
                      </div>
                    )}

                    <div className="tutor-marcas">
                      <EstadoDeInvitacion estado={t.membership_status} />

                      {t.access_level === 'basic' && (
                        // El nombre técnico no se enseña: se enseña lo que implica.
                        <span
                          className="pastilla pastilla-info"
                          title="Ve progreso y avisos; no ve pagos ni ficha médica"
                        >
                          Ve progreso y avisos, no pagos ni ficha médica
                        </span>
                      )}

                      {t.receives_billing && (
                        <span className="pastilla pastilla-info">
                          Recibe la facturación
                        </span>
                      )}

                      {t.can_pickup && (
                        <span className="pastilla pastilla-info">
                          Puede recoger
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

          {/*
            El grado, **antes que la cuota**: es lo que se mira de un alumno todos
            los días, y la cuota se mira el día 1. El entrenador además solo ve
            esto de los dos.
          */}
          {puedeVerGrados(rol) && (
            <>
              <h2 className="seccion">Grado</h2>
              <PasaporteDelAlumno
                club={club}
                alumnoId={alumnoId}
                puedeEditar={puedeOtorgarGrados(rol) && !suspendido}
              />
            </>
          )}

          {/*
            La cuota, detrás de los tutores: «quién paga» sale de un tutor hecho
            pagador, así que el orden de la ficha sigue el orden del trabajo.
          */}
          {puedeVerCuota(rol) && (
            <>
              <h2 className="seccion">Cuota</h2>
              <CuotaDelAlumno
                club={club}
                alumnoId={alumnoId}
                puedeEditar={puedeEditarCuota(rol) && !suspendido}
                alAbrirPagador={alAbrirPagador}
              />
            </>
          )}

          {/*
            Las dos secciones sensibles, al final y con sus propias reglas. El
            asistente no ve ninguna: la médica es categoría especial del RGPD y
            los contactos son teléfonos de familias.
          */}
          {puedeVerContactos(rol) && (
            <>
              <h2 className="seccion">Contactos de emergencia</h2>
              <ContactosDeEmergencia
                club={club}
                alumnoId={alumnoId}
                puedeEditar={puedeEditarContactos(rol) && !suspendido}
              />
            </>
          )}

          {puedeVerFichaMedica(rol) && (
            <>
              <h2 className="seccion">Salud</h2>
              <FichaMedica
                club={club}
                alumnoId={alumnoId}
                puedeEditar={puedeEditarFichaMedica(rol) && !suspendido}
              />

              {/*
                Las lesiones pegadas a la salud y no al final: son lo mismo para
                quien las mira, y separarlas haría que el maestro comprobara en dos
                sitios antes de montar la clase del martes. Mismas reglas de rol.
              */}
              <h2 className="seccion">Lesiones</h2>
              <Lesiones
                club={club}
                alumnoId={alumnoId}
                puedeEditar={puedeEditarFichaMedica(rol) && !suspendido}
              />
            </>
          )}

          {/*
            El pasaporte, al final y para todos los que ven grados: es lo que se
            imprime cuando una familia lo pide en el mostrador.
          */}
          {puedeVerGrados(rol) && (
            <>
              <h2 className="seccion">El pasaporte</h2>
              <PasaporteImprimible club={club} alumnoId={alumnoId} />
            </>
          )}

          {/*
            Lo administrativo al final del todo, y solo para quien lleva la
            administración: son papeles y llaveros, no es lo que se viene a mirar de
            un alumno.
          */}
          {puedeEditarAlumnos(rol) && (
            <>
              <h2 className="seccion">Papeles</h2>
              <PapelesDelAlumno
                club={club}
                alumnoId={alumnoId}
                puedeEditar={!suspendido}
              />

              <h2 className="seccion">Tarjeta de fichaje</h2>
              <TarjetaDeFichaje
                club={club}
                alumnoId={alumnoId}
                puedeEditar={!suspendido}
              />

              {/*
                Las tallas, con los papeles y el llavero: son administración, no es
                lo que se viene a mirar de un alumno. Y las dos rutas declaran `own`
                y `adm`, la misma pareja que el resto de este bloque.
              */}
              <h2 className="seccion">Tallas</h2>
              <Tallas
                club={club}
                alumnoId={alumnoId}
                puedeEditar={!suspendido}
              />

              {/*
                §P.10 · El control de peso, aquí y no en la sección de competición:
                el histórico es **de una persona**, y se mira al lado de su edad y
                de su ficha médica, no dentro de un campeonato concreto.

                La edad se le pasa para poder avisar de la regla de los 16 **antes**
                de que alguien escriba un número. El bloqueo lo aplica el servidor
                igualmente.
              */}
              <h2 className="seccion">Control de peso</h2>
              <ControlDePeso
                club={club}
                alumnoId={alumnoId}
                edad={alumno.data.alumno.age}
                puedeEditar={!suspendido}
              />
            </>
          )}
        </>
      )}
    </section>
  );
}

/**
 * `invited` frente a `active`, dicho en palabras de club.
 *
 * «Pendiente» y no «invitado» porque lo que el club necesita saber es que ahí hay
 * algo que hacer: llamar, o reenviar el correo.
 */
function EstadoDeInvitacion({ estado }: { estado: string }) {
  if (estado === 'active') {
    return <span className="pastilla pastilla-active">Ya usa la app</span>;
  }

  if (estado === 'invited') {
    return (
      <span
        className="pastilla pastilla-paused"
        title="Se le mandó el correo y todavía no ha entrado"
      >
        Invitación pendiente
      </span>
    );
  }

  return <span className="pastilla pastilla-left">{estado}</span>;
}
